import React from 'react';

/**
 * Keep arithmetic multiplication visible. GRE quant copy uses `*`
 * (`1/2 * 12 * 6`) which the emphasis parser would otherwise swallow.
 */
export function preserveQuantMultiplication(text) {
  return String(text)
    .replace(/(\s)\*(\s)/g, '$1×$2')
    .replace(/(\d)\s*\*\s*(\d)/g, '$1 × $2')
    .replace(/([)}\]])\s*\*\s*(\d)/g, '$1 × $2')
    .replace(/(\d)\s*\*\s*([(\[{])/g, '$1 × $2');
}

/**
 * Renders GRE verbal explanations that use markdown-style asterisks for emphasis.
 * Both *word* and **word** become <strong> in the UI (per product copy preference).
 * Also supports underscore emphasis and preserves line breaks from backend content.
 */
export function renderGreExplanationEmphasis(text) {
  if (text == null) return null;
  const s = String(text);
  if (s === '') return '';

  let key = 0;
  const emphasisTokens = ['**', '__', '*', '_'];

  const pushPlainText = (target, value) => {
    const parts = value.split('\n');
    parts.forEach((part, index) => {
      if (part) {
        target.push(part);
      }
      if (index < parts.length - 1) {
        target.push(<br key={`gre-exp-br-${key++}`} />);
      }
    });
  };

  const parseSegment = (input) => {
    const result = [];
    let i = 0;
    while (i < input.length) {
      let earliestIndex = -1;
      let matchedToken = null;

      emphasisTokens.forEach((token) => {
        const index = input.indexOf(token, i);
        if (index !== -1 && (earliestIndex === -1 || index < earliestIndex)) {
          earliestIndex = index;
          matchedToken = token;
        }
      });

      if (earliestIndex === -1 || !matchedToken) {
        pushPlainText(result, input.slice(i));
        break;
      }

      if (earliestIndex > i) {
        pushPlainText(result, input.slice(i, earliestIndex));
      }

      const closeIndex = input.indexOf(
        matchedToken,
        earliestIndex + matchedToken.length
      );

      if (closeIndex === -1) {
        pushPlainText(result, input.slice(earliestIndex));
        break;
      }

      result.push(
        <strong key={`gre-exp-${key++}`}>
          {parseSegment(
            input.slice(earliestIndex + matchedToken.length, closeIndex)
          )}
        </strong>
      );

      i = closeIndex + matchedToken.length;
    }

    return result;
  };

  return parseSegment(s);
}

export function renderGreQuantExplanation(text) {
  if (text == null) return null;
  return renderGreExplanationEmphasis(preserveQuantMultiplication(text));
}
